import React, { useState } from "react";
import { Layout, Menu, Avatar, Dropdown, Grid, Button, Drawer } from "antd";
import {
    DashboardOutlined,
    TeamOutlined,
    UsergroupAddOutlined,
    CalendarOutlined,
    ReadOutlined,
    FileTextOutlined,
    UserOutlined,
    LogoutOutlined,
    MenuOutlined,
    BellOutlined,
    IdcardOutlined,
    MessageOutlined,
    ApartmentOutlined,
    SafetyCertificateOutlined,
    FlagOutlined,
    PictureOutlined,
    GiftOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

// key = absolute route; roles = who can see it.
const MENU = [
    { key: "/admin", icon: <DashboardOutlined />, label: "Dashboard", roles: ["SUPER_ADMIN", "GROUP_ADMIN"] },
    { key: "/admin/users", icon: <UserOutlined />, label: "Users", roles: ["SUPER_ADMIN"] },
    { key: "/admin/groups", icon: <UsergroupAddOutlined />, label: "Teams / Groups", roles: ["SUPER_ADMIN"] },
    { key: "/admin/notifications", icon: <BellOutlined />, label: "Notifications", roles: ["SUPER_ADMIN"] },
    { key: "/admin/certificates", icon: <SafetyCertificateOutlined />, label: "Certificates", roles: ["SUPER_ADMIN"] },
    { key: "/admin/organizations", icon: <ApartmentOutlined />, label: "Organizations", roles: ["SUPER_ADMIN"] },
    { key: "/admin/feedback", icon: <MessageOutlined />, label: "Feedback", roles: ["SUPER_ADMIN"] },
    { key: "/admin/reports", icon: <FlagOutlined />, label: "Moderation", roles: ["SUPER_ADMIN"] },
    { key: "/admin/my-group", icon: <IdcardOutlined />, label: "My Group", roles: ["SUPER_ADMIN", "GROUP_ADMIN"] },
    { key: "/admin/my-team", icon: <TeamOutlined />, label: "My Team", roles: ["SUPER_ADMIN", "GROUP_ADMIN"] },
    { key: "/admin/events", icon: <CalendarOutlined />, label: "Events", roles: ["SUPER_ADMIN"] },
    { key: "/admin/news", icon: <ReadOutlined />, label: "News", roles: ["SUPER_ADMIN"] },
    { key: "/admin/portfolio", icon: <PictureOutlined />, label: "Portfolio", roles: ["SUPER_ADMIN"] },
    { key: "/admin/gifts", icon: <GiftOutlined />, label: "Gifts", roles: ["SUPER_ADMIN"] },
    { key: "/admin/posts", icon: <FileTextOutlined />, label: "Posts", roles: ["SUPER_ADMIN", "GROUP_ADMIN"] },
];

const AdminLayout = ({ children }) => {
    const { admin, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    const onHamburger = () =>
        isMobile ? setDrawerOpen(true) : setCollapsed((c) => !c);

    const items = MENU.filter((m) => m.roles.includes(admin.role)).map(
        ({ key, icon, label }) => ({ key, icon, label })
    );

    // Longest matching key so nested paths still highlight correctly.
    const selectedKey =
        [...items]
            .map((i) => i.key)
            .filter((k) => location.pathname === k || location.pathname.startsWith(k + "/"))
            .sort((a, b) => b.length - a.length)[0] || location.pathname;

    const onSelect = ({ key }) => {
        navigate(key);
        setDrawerOpen(false);
    };

    const menu = (
        <Menu
            mode="inline"
            theme="dark"
            selectedKeys={[selectedKey]}
            items={items}
            onClick={onSelect}
            style={{ borderInlineEnd: 0 }}
        />
    );

    const userMenu = {
        items: [
            { key: "role", disabled: true, label: `Role: ${admin.role}` },
            { type: "divider" },
            { key: "logout", icon: <LogoutOutlined />, label: "Logout", danger: true },
        ],
        onClick: ({ key }) => key === "logout" && logout(),
    };

    return (
        <Layout className="admin-shell">
            {!isMobile && (
                <Sider
                    theme="dark"
                    width={240}
                    collapsedWidth={80}
                    collapsed={collapsed}
                    className="admin-sider"
                >
                    <div className="admin-brand">
                        {collapsed ? "CE" : "Ctrl Events"}
                    </div>
                    {menu}
                </Sider>
            )}

            <Layout>
                <Header className="admin-topbar">
                    <Button
                        type="text"
                        icon={<MenuOutlined />}
                        onClick={onHamburger}
                        aria-label="Toggle navigation"
                    />
                    <div className="admin-topbar__spacer" />
                    <Dropdown menu={userMenu} trigger={["click"]}>
                        <div className="admin-user">
                            <Avatar
                                size="small"
                                src={admin.avatar}
                                icon={<UserOutlined />}
                            />
                            <span className="admin-user__name">
                                {admin.name || admin.email}
                            </span>
                        </div>
                    </Dropdown>
                </Header>

                <Content className="admin-content">{children}</Content>
            </Layout>

            <Drawer
                placement="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                width={240}
                styles={{ body: { padding: 0, background: "#001529" } }}
                closable={false}
            >
                <div className="admin-brand">Ctrl Events</div>
                {menu}
            </Drawer>
        </Layout>
    );
};

export default AdminLayout;
